import { useRef, useState } from "react";
import { ChevronDown, CornerUpLeft, FileText, ListPlus, Pencil, Pin, Tag, Trash2, Copy, Ban } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { api, fileUrl, formatErr } from "@/lib/api";
import { fmtDateTime, fmtTime } from "@/lib/format";
import { UserAvatar } from "@/components/common";
import { cn } from "@/lib/utils";

export const EMOJIS = ["👍", "❤️", "😂", "😮", "🙏", "✅", "🔥", "👏"];
const call = (p) => p.catch((e) => toast.error(formatErr(e)));

function RichText({ text, rx }) {
  if (!rx) return text;
  return text.split(rx).map((part, i) => (i % 2 === 1 ? <span key={i} className="font-semibold text-primary">{part}</span> : part));
}

function Poll({ m, me, userMap, onUpdate }) {
  const p = m.poll;
  const closed = p.deadline && new Date(p.deadline) < new Date();
  const total = p.total_voters || 0;
  const mine = p.anonymous ? p.my_votes || [] : p.options.filter((o) => o.votes?.includes(me.id)).map((o) => o.id);
  const voted = mine.length > 0;
  const pick = (id) => {
    if (closed) return;
    const next = p.multiple ? (mine.includes(id) ? mine.filter((x) => x !== id) : [...mine, id]) : [id];
    api.post(`/chat/messages/${m.id}/vote`, { option_ids: next }).then((r) => onUpdate?.(r.data)).catch((e) => toast.error(formatErr(e)));
  };
  return (
    <div className="min-w-[230px] space-y-2" data-testid={`poll-${m.id}`}>
      <p className="font-semibold">{p.question}</p>
      <p className="text-[11px] text-muted-foreground">{p.multiple ? "Select one or more" : "Select one"}{p.anonymous ? " · Anonymous" : ""}{p.deadline ? ` · ${closed ? "Closed" : "Closes"} ${fmtDateTime(p.deadline)}` : ""}</p>
      {p.options.map((o) => {
        const pct = total ? Math.round((o.count / total) * 100) : 0;
        return (
          <button key={o.id} type="button" disabled={closed} onClick={() => pick(o.id)} data-testid={`poll-option-${m.id}-${o.id}`} className="block w-full rounded-lg border bg-card/70 p-2 text-left disabled:cursor-default">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2">
                <span className={cn("h-4 w-4 shrink-0 border-2", p.multiple ? "rounded" : "rounded-full", mine.includes(o.id) ? "border-primary bg-primary" : "border-muted-foreground/50")} />
                {o.text}
              </span>
              <span className="num text-xs">{pct}%</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-secondary"><div className="h-1.5 rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} /></div>
            {!p.anonymous && o.votes.length > 0 && <p className="mt-1 text-[11px] text-muted-foreground">{o.votes.map((v) => userMap[v]?.name || "?").join(", ")}</p>}
          </button>
        );
      })}
      <p className="text-[11px] text-muted-foreground" data-testid={`poll-total-${m.id}`}>{total} voted{voted && p.anonymous ? " · You voted" : ""}</p>
    </div>
  );
}

function Checklist({ m }) {
  const c = m.checklist;
  const done = c.items.filter((i) => i.done).length;
  return (
    <div className="min-w-[230px] space-y-2" data-testid={`checklist-${m.id}`}>
      <p className="font-semibold">{c.title} <span className="num text-xs font-normal text-muted-foreground">{done}/{c.items.length}</span></p>
      {c.items.map((it) => (
        <label key={it.id} className="flex cursor-pointer items-start gap-2 text-sm">
          <Checkbox checked={it.done} onCheckedChange={() => call(api.post(`/chat/messages/${m.id}/checklist/${it.id}/toggle`))} data-testid={`checklist-item-${m.id}-${it.id}`} className="mt-0.5" />
          <span>
            <span className={cn(it.done && "text-muted-foreground line-through")}>{it.text}</span>
            {it.done && <span className="block text-[11px] text-muted-foreground">{it.done_by_name} · {fmtDateTime(it.done_at)}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

export default function MessageBubble({ m, me, userMap, now, rx, highlight, onReply, onEdit, onTask, onTag, onJump, onUpdate }) {
  const [menu, setMenu] = useState(false);
  const press = useRef(null);
  const own = m.sender_id === me.id;
  const sender = userMap[m.sender_id] || { name: m.sender_name, initials: "?", color: "#94A3B8" };
  const inWindow = now < new Date(m.editable_until).getTime();
  const canChange = !m.deleted && (me.role === "admin" || (own && inWindow));
  const color = sender.color || "#94A3B8";
  const touchStart = () => { press.current = setTimeout(() => setMenu(true), 500); };
  const touchEnd = () => clearTimeout(press.current);

  return (
    <div id={`msg-${m.id}`} data-testid={`chat-message-${m.id}`} className={cn("group flex gap-2", own ? "flex-row-reverse" : "flex-row")}>
      {!own && <UserAvatar user={sender} size="md" className="mt-1" />}
      <div
        onContextMenu={(e) => { e.preventDefault(); setMenu(true); }}
        onTouchStart={touchStart} onTouchEnd={touchEnd} onTouchMove={touchEnd}
        className={cn("relative max-w-[85%] rounded-2xl border px-3 py-2 sm:max-w-[70%]", own ? "rounded-tr-sm" : "rounded-tl-sm", highlight && "ring-2 ring-accent")}
        style={{ background: `${color}1A`, borderColor: `${color}55` }}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold" style={{ color }}>{own ? "You" : sender.name}</span>
          <DropdownMenu open={menu} onOpenChange={setMenu}>
            <DropdownMenuTrigger data-testid={`message-menu-${m.id}`} className="-mr-1 rounded p-0.5 text-muted-foreground opacity-60 hover:bg-background/60 group-hover:opacity-100"><ChevronDown className="h-4 w-4" /></DropdownMenuTrigger>
            <DropdownMenuContent align={own ? "end" : "start"} className="w-56" onCloseAutoFocus={(e) => e.preventDefault()}>
              {!m.deleted && <div className="grid grid-cols-8 px-0.5 py-1">{EMOJIS.map((e) => <button key={e} data-testid={`react-${m.id}-${e}`} onClick={() => { setMenu(false); call(api.post(`/chat/messages/${m.id}/react`, { emoji: e })); }} className="rounded p-0.5 text-base hover:bg-secondary">{e}</button>)}</div>}
              <DropdownMenuSeparator />
              {!m.deleted && <DropdownMenuItem data-testid={`message-reply-${m.id}`} onClick={() => onReply(m)}><CornerUpLeft className="mr-2 h-4 w-4" />Reply</DropdownMenuItem>}
              {canChange && m.type === "text" && <DropdownMenuItem data-testid={`message-edit-${m.id}`} onClick={() => onEdit(m)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>}
              {!m.deleted && <DropdownMenuItem data-testid={`message-task-${m.id}`} onClick={() => onTask(m)}><ListPlus className="mr-2 h-4 w-4" />Create task</DropdownMenuItem>}
              {!m.deleted && <DropdownMenuItem data-testid={`message-pin-${m.id}`} onClick={() => call(api.post(`/chat/messages/${m.id}/pin`))}><Pin className="mr-2 h-4 w-4" />{m.pinned ? "Unpin" : "Pin"}</DropdownMenuItem>}
              {!m.deleted && <DropdownMenuItem data-testid={`message-tag-${m.id}`} onClick={() => onTag(m)}><Tag className="mr-2 h-4 w-4" />Tag to meeting</DropdownMenuItem>}
              {m.text && !m.deleted && <DropdownMenuItem onClick={() => navigator.clipboard?.writeText(m.text)}><Copy className="mr-2 h-4 w-4" />Copy</DropdownMenuItem>}
              {canChange && <DropdownMenuItem data-testid={`message-delete-${m.id}`} className="text-destructive" onClick={() => call(api.delete(`/chat/messages/${m.id}`))}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {m.reply_preview && !m.deleted && (
          <button onClick={() => onJump(m.reply_preview.id)} className="mt-1 block w-full rounded-lg border-l-4 bg-background/60 px-2 py-1 text-left text-xs" style={{ borderLeftColor: userMap[m.reply_preview.sender_id]?.color }} data-testid={`reply-preview-${m.id}`}>
            <span className="font-semibold">{m.reply_preview.sender_name}</span>
            <span className="line-clamp-2 text-muted-foreground">{m.reply_preview.text}</span>
          </button>
        )}
        <div className="mt-1 text-sm">
          {m.deleted ? (
            <span className="flex items-center gap-1 italic text-muted-foreground" data-testid={`message-deleted-${m.id}`}><Ban className="h-3.5 w-3.5" />This message was deleted</span>
          ) : m.type === "poll" ? <Poll m={m} me={me} userMap={userMap} onUpdate={onUpdate} /> : m.type === "checklist" ? <Checklist m={m} /> : (
            <>
              {m.text && <p className="whitespace-pre-wrap break-words" data-testid={`message-text-${m.id}`}><RichText text={m.text} rx={rx} /></p>}
              {m.attachments?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {m.attachments.map((a) => (a.content_type?.startsWith("image/") ? (
                    <a key={a.path} href={fileUrl(a.path)} target="_blank" rel="noreferrer"><img src={fileUrl(a.path)} alt={a.name} className="h-36 max-w-[220px] rounded-lg object-cover" /></a>
                  ) : (
                    <a key={a.path} href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border bg-background/70 px-3 py-2 text-xs"><FileText className="h-4 w-4 text-primary" />{a.name}</a>
                  )))}
                </div>
              )}
            </>
          )}
        </div>
        {Object.keys(m.reactions || {}).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {Object.entries(m.reactions).map(([e, us]) => (
              <button key={e} title={us.map((u) => userMap[u]?.name).join(", ")} onClick={() => call(api.post(`/chat/messages/${m.id}/react`, { emoji: e }))} data-testid={`reaction-${m.id}-${e}`}
                className={cn("rounded-full border bg-background/80 px-1.5 text-xs", us.includes(me.id) && "border-primary")}>{e} {us.length}</button>
            ))}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center justify-end gap-1.5 text-[10px] text-muted-foreground">
          {m.meeting_tag && <span className="rounded bg-background/70 px-1.5" data-testid={`message-meeting-${m.id}`}>{m.meeting_tag}</span>}
          {m.task_ids?.length > 0 && <span className="rounded bg-background/70 px-1.5">{m.task_ids.length} task{m.task_ids.length > 1 ? "s" : ""}</span>}
          {m.pinned && <Pin className="h-3 w-3" data-testid={`message-pinned-${m.id}`} />}
          {m.edited && !m.deleted && <span data-testid={`message-edited-${m.id}`}>edited</span>}
          <span>{fmtTime(m.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
